import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/lib/components/DropdownMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { useElementMotionStore } from "@/lib/stores/element-motion-store/element-motion-store";
import {
  IconArrowsMaximize,
  IconChevronDown,
  IconChevronsDown,
  IconChevronsUp,
  IconChevronUp,
  IconCopyPlus,
  IconDots,
  IconLayoutAlignBottom,
  IconLayoutAlignCenter,
  IconLayoutAlignLeft,
  IconLayoutAlignMiddle,
  IconLayoutAlignRight,
  IconLayoutAlignTop,
  IconStack2,
  IconTrash,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";
import { forwardRef } from "react";
import { useCurrentScale } from "remotion";
import {
  useDuplicateElements,
  useSetSelectionFullscreen,
  useToggleSelectionVisibility,
  useUpdateElementOrdering,
} from "./element-hooks";

export type AlignType =
  | "left"
  | "center"
  | "right"
  | "top"
  | "middle"
  | "bottom";

export type OrderingType =
  | "bring-to-front"
  | "bring-forward"
  | "send-backward"
  | "send-to-back";

export default function SelectionActions({
  isMoving,
  isResizing,
}: {
  isMoving: boolean;
  isResizing: boolean;
}) {
  const scale = useCurrentScale() || 1;

  const deleteSelectedElements = useElementMotionStore(
    (state) => state.service.deleteSelectedElements,
  );

  const { isVisible: isVisibleFullscreen, setSelectionFullscreen } =
    useSetSelectionFullscreen();

  const duplicateSelectedElements = useDuplicateElements();

  const { allVisible, toggleSelectionVisibility } =
    useToggleSelectionVisibility();

  if (isMoving || isResizing) {
    return null;
  }

  return (
    <div
      className="absolute flex cursor-pointer items-center justify-center overflow-hidden border-border bg-white text-text-primary hover:bg-button-outline-hover"
      style={{
        bottom: `calc(100% + ${12 / scale}px)`,
        left: "50%",
        transform: "translateX(-50%)",
        borderWidth: 1 / scale,
        borderRadius: 7 / scale,
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      {isVisibleFullscreen && (
        <ActionItem
          icon={<IconArrowsMaximize size={18 / scale} />}
          tooltipLabel="Make fullscreen"
          scale={scale}
          onClick={setSelectionFullscreen}
        />
      )}

      <ActionItem
        icon={<IconCopyPlus size={18 / scale} />}
        tooltipLabel="Duplicate"
        scale={scale}
        onClick={duplicateSelectedElements}
      />
      <ActionItem
        icon={<IconTrash size={18 / scale} />}
        tooltipLabel="Delete"
        scale={scale}
        onClick={deleteSelectedElements}
      />
      <ActionItem
        icon={
          allVisible ? (
            <IconEye size={18 / scale} />
          ) : (
            <IconEyeOff size={18 / scale} />
          )
        }
        tooltipLabel={allVisible ? "Hide" : "Show"}
        scale={scale}
        onClick={toggleSelectionVisibility}
      />

      <DropdownMenu>
        <ActionItem
          icon={<IconDots size={18 / scale} />}
          tooltipLabel="More actions"
          scale={scale}
          onClick={() => {}}
          itemType="dropdown-menu"
        />
        <DropdownMenuContent
          className="w-52 data-[state=closed]:!animate-none"
          side="bottom"
          align="start"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        >
          {isVisibleFullscreen && (
            <DropdownMenuItem onClick={setSelectionFullscreen}>
              <IconArrowsMaximize />
              <span>Make fullscreen</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={duplicateSelectedElements}>
            <IconCopyPlus />
            <span>Duplicate</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <IconLayoutAlignLeft />
              <span>Align</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-44">
              <AlignItem alignType="left" />
              <AlignItem alignType="center" />
              <AlignItem alignType="right" />
              <DropdownMenuSeparator />
              <AlignItem alignType="top" />
              <AlignItem alignType="middle" />
              <AlignItem alignType="bottom" />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <IconStack2 />
              <span>Layer</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-44">
              <OrderingItem ordering="bring-to-front" />
              <OrderingItem ordering="bring-forward" />
              <OrderingItem ordering="send-backward" />
              <OrderingItem ordering="send-to-back" />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={deleteSelectedElements}>
            <IconTrash />
            <span>Delete</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={toggleSelectionVisibility}>
            {allVisible ? <IconEye /> : <IconEyeOff />}
            <span>{allVisible ? "Hide" : "Show"}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const alignIcons: Record<AlignType, React.ElementType> = {
  left: IconLayoutAlignLeft,
  center: IconLayoutAlignCenter,
  right: IconLayoutAlignRight,
  top: IconLayoutAlignTop,
  middle: IconLayoutAlignMiddle,
  bottom: IconLayoutAlignBottom,
};

const alignLabels: Record<AlignType, string> = {
  left: "Left",
  center: "Center",
  right: "Right",
  top: "Top",
  middle: "Middle",
  bottom: "Bottom",
};

function AlignItem({ alignType }: { alignType: AlignType }) {
  const Icon = alignIcons[alignType];
  const label = alignLabels[alignType];

  const alignSelectedElements = useElementMotionStore(
    (state) => state.service.alignSelectedElements,
  );

  return (
    <DropdownMenuItem onClick={() => alignSelectedElements(alignType)}>
      <Icon />
      <span>{label}</span>
    </DropdownMenuItem>
  );
}

const orderingIcons: Record<OrderingType, React.ElementType> = {
  "bring-to-front": IconChevronsUp,
  "bring-forward": IconChevronUp,
  "send-backward": IconChevronDown,
  "send-to-back": IconChevronsDown,
};

const orderingLabels: Record<OrderingType, string> = {
  "bring-to-front": "Bring to front",
  "bring-forward": "Bring forward",
  "send-backward": "Send backward",
  "send-to-back": "Send to back",
};

function OrderingItem({ ordering }: { ordering: OrderingType }) {
  const Icon = orderingIcons[ordering];
  const label = orderingLabels[ordering];

  const updateElementOrdering = useUpdateElementOrdering();

  return (
    <DropdownMenuItem onClick={() => updateElementOrdering(ordering)}>
      <Icon />
      <span>{label}</span>
    </DropdownMenuItem>
  );
}

type ActionItemProps = {
  icon: React.ReactNode;
  tooltipLabel: string;
  scale: number;
  onClick: () => void;
  itemType?: "button" | "dropdown-menu";
};

const ActionItem = forwardRef<HTMLButtonElement, ActionItemProps>(
  ({ icon, tooltipLabel, scale, onClick, itemType = "button" }, ref) => {
    const button = (
      <button
        className="flex cursor-pointer items-center justify-center bg-white text-text-primary hover:bg-button-outline-hover focus-visible:outline-none"
        onClick={onClick}
        style={{
          width: 32 / scale,
          height: 32 / scale,
        }}
      >
        {icon}
      </button>
    );

    let trigger = button;

    if (itemType === "dropdown-menu") {
      trigger = <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild ref={ref}>
          {trigger}
        </TooltipTrigger>
        <TooltipContent>{tooltipLabel}</TooltipContent>
      </Tooltip>
    );
  },
);
